import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/compat/router';

import { getLogger } from 'src/utils/datadog-logger';
import { tracker } from 'src/utils/analytics';

// FIXME: we are not suppose to import things form the internal of the analytics framework. we need to rework the trackerSource implementation to make is agnostic of GA
import {
  ProductRecommendationSection,
  ProductRecommendationSource,
} from 'src/utils/analytics/trackers/internal-gtm-tracker/internal-gtm-tracker.types';
import { getItemListName } from 'src/utils/analytics/trackers/internal-gtm-tracker/helpers/item-list-name';

import type { GetSponsoredBrandQuery, AdTrackers } from 'src/gql/graphql';
import { ProductPlacement } from 'src/types';
import { Product, parseProduct } from 'src/utils/helpers/product';
import { AdPlacement } from '../placements';
import { useFetchTracker } from './use-fetch-tracker';

const logger = getLogger('ads.sponsored-brand');

type SponsoredBrandData = {
  campaignId: string | undefined;
  brandName: string | undefined;
  globalTrackers: AdTrackers | undefined;
  products: Array<Product>;
};

export function useSponsoredBrandData(data: GetSponsoredBrandQuery | null | undefined): SponsoredBrandData {
  const { name: brandName, campaignId, products = [], trackers: globalTrackers } = data?.getSponsoredBrand ?? {};

  const normalizedProducts = useMemo(() => products.map((item): Product => parseProduct(item)), [products]);

  return {
    brandName,
    campaignId,
    globalTrackers,
    products: normalizedProducts,
  };
}

export type SponsoredBrandHandlersParams = SponsoredBrandData & {
  hasBeenVisible: boolean;
  placement: ProductPlacement;
  adPlacement: AdPlacement;
};

type SponsoredBrandHandlers = {
  handleProductClick: (product: Product, index: number) => void;
  handleQuickAddToCart: (product: Product, index: number) => void;
  handleProductImpression: (product: Product, index: number) => void;
  handleViewAllClick: () => void;
};

export function useSponsoredBrandHandlers({
  products = [],
  campaignId,
  globalTrackers,
  hasBeenVisible,
  placement,
  adPlacement,
}: SponsoredBrandHandlersParams): SponsoredBrandHandlers {
  const router = useRouter();
  const fetchTracker = useFetchTracker();

  const { impressionUrl, clickUrl } = globalTrackers ?? {};
  const hasFiredBillingClick = useRef(false);

  const trackerSource = router
    ? getItemListName({
        router,
        source: ProductRecommendationSource.sponsored,
        section: ProductRecommendationSection.sponsoredBrand,
      })
    : '';

  useEffect(() => {
    hasFiredBillingClick.current = false;
  }, [campaignId]);

  const handleBannerImpression = useCallback(() => {
    if (impressionUrl) {
      logger.info('handleBannerImpression', { impressionUrl, campaignId, trackerSource });

      void fetchTracker(impressionUrl, {
        uiPlacement: adPlacement,
        uiPlacementIndex: -1,
        trackingParams: {
          productId: products.map((p) => p.id ?? 'unknown').join(','),
          component: products.map((_, i) => `card${i + 1}`).join(','),
        },
      });
      tracker.sponsoredBannerImpression({ impressionUrl, products, campaignId, trackerSource });
    }
  }, [adPlacement, campaignId, fetchTracker, impressionUrl, products, trackerSource]);

  const handleBannerClick = useCallback(
    (trackingParams?: { productId?: string; component?: string; position?: string }) => {
      if (!clickUrl || hasFiredBillingClick.current) return;
      hasFiredBillingClick.current = true;

      logger.info('handleBannerClick', { clickUrl, campaignId, trackerSource });

      void fetchTracker(clickUrl, {
        uiPlacement: adPlacement,
        uiPlacementIndex: -1,
        trackingParams,
      });

      tracker.sponsoredBannerClicked({ clickUrl, products, campaignId, trackerSource });
    },
    [clickUrl, campaignId, trackerSource, fetchTracker, adPlacement, products]
  );

  // TODO: we should delegrate the lifecycle of the banner impression to the component that renders it
  useEffect(() => {
    if (hasBeenVisible) {
      handleBannerImpression();
    }
  }, [handleBannerImpression, hasBeenVisible]);

  // Sponsored Brand Product card view
  const handleProductImpression = useCallback(
    (product: Product, index: number) => {
      const { impressionUrl: productImpressionUrl } = product.adTrackers ?? {};

      if (productImpressionUrl) {
        logger.info('handleProductImpression', { impressionUrl: productImpressionUrl, campaignId, trackerSource });

        void fetchTracker(productImpressionUrl, {
          uiPlacement: adPlacement,
          uiPlacementIndex: index,
          trackingParams: {
            component: `card${index + 1}`,
            position: String(index + 1),
          },
        });

        tracker.sponsoredProductImpression({ product, campaignId, trackerSource, position: index });
      }
    },
    [campaignId, trackerSource, fetchTracker, adPlacement]
  );

  // Sponsored Brand Product card click
  const handleProductClick = useCallback(
    (product: Product, index: number): void => {
      handleBannerClick({
        productId: product.id ?? undefined,
        component: `card${index + 1}`,
        position: String(index + 1),
      });

      const { clickUrl: productClickUrl } = product.adTrackers ?? {};

      if (productClickUrl) {
        logger.info('handleProductClick', { clickUrl: productClickUrl, campaignId, trackerSource });

        void fetchTracker(productClickUrl, {
          uiPlacement: adPlacement,
          uiPlacementIndex: index,
          trackingParams: {
            component: `card${index + 1}`,
            position: String(index + 1),
          },
        });

        tracker.sponsoredProductClicked({ product, campaignId, trackerSource, position: index });
        tracker.setContext({ activePromotion: { product, campaignId, trackerSource } });
      }

      // FIXME: why do we care about trackerSource in productClick?
      tracker.productClicked({ product, position: index, trackerSource, placement });
    },
    [handleBannerClick, trackerSource, placement, campaignId, fetchTracker, adPlacement]
  );

  return {
    handleProductClick,
    handleProductImpression,
    handleQuickAddToCart: handleProductClick,
    handleViewAllClick: () => handleBannerClick(),
  };
}
